import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPartner, getPartners, updatePartner, getPartnerByReferralId } from '../services/partners';
import { Partner, PartnerPayload, PartnersParams, PartnersResponse } from '../types/partner';

export const PARTNERS_QUERY_KEY = 'partners';
export const PARTNER_BY_REFERRAL_QUERY_KEY = 'partnerByReferral';

export const usePartners = (params: PartnersParams) =>
  useQuery<PartnersResponse>({
    queryKey: [PARTNERS_QUERY_KEY, params.page, params.pageSize, params.search],
    queryFn: () => getPartners(params),
  });

export const useCreatePartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PartnerPayload) => createPartner(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNERS_QUERY_KEY] });
    },
  });
};

export const useUpdatePartner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ partnerId, payload }: { partnerId: number; payload: PartnerPayload }) =>
      updatePartner(partnerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNERS_QUERY_KEY] });
    },
  });
};

/**
 * Downloads the image off-screen so that whoever renders it paints it from
 * cache on the first frame instead of popping it in seconds later.
 */
const usePreloadedImage = (url: string | null | undefined) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(false);
    if (!url) return undefined;

    let isStale = false;
    const image = new Image();
    // A logo that 404s must not hold the page hostage, so an error counts as
    // done too — CoBrandedLogo drops the image once it fails to load.
    const markLoaded = () => {
      if (!isStale) setIsLoaded(true);
    };
    image.onload = markLoaded;
    image.onerror = markLoaded;
    image.src = url;
    if (image.complete) markLoaded();

    return () => {
      isStale = true;
    };
  }, [url]);

  return isLoaded;
};

// An unknown referral_id is a 404, which is the normal case on signup — don't
// retry it, and let the caller treat "no data" as "show the Marco logo alone".
export const usePartnerByReferralId = (referralId: string | null | undefined) => {
  const { data, isInitialLoading, isFetched } = useQuery<Partner>({
    queryKey: [PARTNER_BY_REFERRAL_QUERY_KEY, referralId],
    queryFn: () => getPartnerByReferralId(referralId!),
    enabled: !!referralId,
    retry: false,
    // A partner logo doesn't change while a form is being filled in, and an
    // errored query is always stale — focus alone would refetch it every time.
    refetchOnWindowFocus: false,
  });

  const isLogoPreloaded = usePreloadedImage(data?.logoUrl);

  return {
    partner: data,
    // isInitialLoading, not isLoading: a disabled query sits in the `loading`
    // status forever, so a page opened without a referral_id would never stop
    // waiting for a request that is never made. The logo download is part of
    // the wait — otherwise the page paints before the image arrives.
    // Latched on isFetched because a query that only ever errored has no
    // dataUpdatedAt, so any refetch puts it back in `loading` — which would
    // swap the page for its skeleton again and unmount the form behind it.
    isLoadingPartner: (isInitialLoading && !isFetched) || (!!data?.logoUrl && !isLogoPreloaded),
  };
};
